import { Box, Container, Typography, Stack, styled } from '@mui/material';
import React from 'react';

const SecurityWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: '#F8F9FA',
  padding: theme.spacing(10, 0),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(8, 0),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(6, 0),
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: '"Nohemi", sans-serif',
  fontWeight: 600,
  fontSize: '42px',
  lineHeight: 1.2,
  color: '#181D27',
  textAlign: 'center',
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('md')]: {
    fontSize: '36px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '28px',
  },
}));

const SectionSubtitle = styled(Typography)(({ theme }) => ({
  fontFamily: '"Inter", sans-serif',
  fontWeight: 400,
  fontSize: '18px',
  lineHeight: 1.6,
  color: '#535862',
  textAlign: 'center',
  marginBottom: theme.spacing(8),
  maxWidth: '700px',
  margin: '0 auto',
  [theme.breakpoints.down('md')]: {
    marginBottom: theme.spacing(6),
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '16px',
    marginBottom: theme.spacing(4),
  },
}));

const SecurityCard = styled(Box)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  borderRadius: '20px',
  padding: theme.spacing(4),
  height: '100%',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  border: '1px solid #F1F3F4',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
  },
}));

const SecurityIcon = styled(Box)(({ theme }) => ({
  width: '64px',
  height: '64px',
  borderRadius: '16px',
  backgroundColor: '#FEF2F2',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(3),
  fontSize: '28px',
}));

const SecurityTitle = styled(Typography)(({ theme }) => ({
  fontFamily: '"Nohemi", sans-serif',
  fontWeight: 600,
  fontSize: '24px',
  lineHeight: 1.3,
  color: '#181D27',
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    fontSize: '20px',
  },
}));

const SecurityDescription = styled(Typography)(({ theme }) => ({
  fontFamily: '"Inter", sans-serif',
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: 1.6,
  color: '#535862',
  marginBottom: theme.spacing(2),
}));

const TechBadge = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  backgroundColor: '#E8F5E8',
  color: '#2E7D32',
  padding: theme.spacing(0.5, 1.5),
  borderRadius: '20px',
  fontSize: '12px',
  fontWeight: 600,
  fontFamily: '"Inter", sans-serif',
  margin: theme.spacing(0.25),
}));

const PMSecuritySection = React.forwardRef<HTMLDivElement>((props, ref) => {
  const securityFeatures = [
    {
      icon: '🔒',
      title: 'Enterprise-Grade Document Storage',
      description:
        'Your sensitive property documents are stored using Google Cloud Storage infrastructure, the same technology trusted by millions of businesses worldwide. All files are encrypted both in transit and at rest using AES-256 encryption standards.',
      technologies: ['Google Cloud Storage', 'AES-256 Encryption', 'SOC 2 Compliant'],
    },
    {
      icon: '🛡️',
      title: 'Secure Database Protection',
      description:
        'Property data is safeguarded in Supabase, a PostgreSQL-based platform built on industry-leading security practices. Every database query is protected with row-level security (RLS) policies and real-time audit logging.',
      technologies: ['Supabase PostgreSQL', 'Row-Level Security', 'Audit Logging'],
    },
    {
      icon: '🔐',
      title: 'Multi-Layer Access Control',
      description:
        'We implement zero-trust architecture with multi-factor authentication (MFA), role-based access control (RBAC), and session management. Your data is only accessible to authorized users with proper verification.',
      technologies: ['Multi-Factor Auth', 'Zero-Trust Security', 'RBAC'],
    },
  ];

  return (
    <SecurityWrapper ref={ref}>
      <Container maxWidth="lg">
        <SectionTitle>Your Property Data is Safe & Secure</SectionTitle>

        <SectionSubtitle>
          We understand that your property documents contain sensitive financial and personal
          information. That's why we've built our platform using enterprise-grade security
          infrastructure that meets the highest industry standards.
        </SectionSubtitle>

        <Stack spacing={4} direction={{ xs: 'column', md: 'row' }} sx={{ mt: 4, mb: 6 }}>
          {securityFeatures.map(feature => (
            <Box key={feature.title} sx={{ flex: 1 }}>
              <SecurityCard>
                <SecurityIcon>{feature.icon}</SecurityIcon>

                <SecurityTitle>{feature.title}</SecurityTitle>

                <SecurityDescription>{feature.description}</SecurityDescription>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 2 }}>
                  {feature.technologies.map(tech => (
                    <TechBadge key={tech}>{tech}</TechBadge>
                  ))}
                </Box>
              </SecurityCard>
            </Box>
          ))}
        </Stack>

        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontSize: '16px',
              color: '#535862',
              fontWeight: 700,
              marginBottom: 2,
            }}
          >
            Trusted by property owners who value security and privacy
          </Typography>
        </Box>
      </Container>
    </SecurityWrapper>
  );
});

export default PMSecuritySection;
